import profileApi from '../../../app/api/profileApi';
import React, { useEffect, useState } from 'react';
import { ResizableDataGrid } from '../../../components/data-grid';
import { useAuth } from 'react-oidc-context';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Card from '@mui/material/Card';
import SuspenseDiv from '../../../layouts/SuspenseDiv';
import Grid from '@mui/material/Unstable_Grid2';
import { styled } from '@mui/material/styles';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Box, Typography, Stack } from '@mui/material';
import { useAvailableActions } from '../../../hooks/useAvailableActions';
import { ProfileListIdentityCell, createProfileIdentityColumn } from '../sections/profile-list-identity-cell';

const ProfilesListSkillkoTableInductionComplete = ({ site, searchQuery = '' }) => {
  const [profiles, setProfiles] = useState([]);
  const [filteredProfiles, setFilteredProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const auth = useAuth();
  const navigate = useNavigate();
  const isSkillkoImportAllowed = process.env.REACT_APP_IS_SKILLKO_IMPORT_ALLOWED === 'true';
  const { account } = useSelector((state) => state.app);
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const {canCreateProfile} = useAvailableActions();

  useEffect(() => {
    const fetchProfiles = async () => {
      profileApi
        .getProfilesFromSkillko(auth.user.access_token, {
          siteId: site.id,
          accountId: account.id,
        })
        .then((r) => {
          const data = r.data.filter((x) => x.isReadyForInduction === true);
          setProfiles(data);
          setFilteredProfiles(data);
          setLoading(false);
        });
    };

    if (isSkillkoImportAllowed) {
      fetchProfiles();
    } else {
      setLoading(false);
    }
  }, []);

  // Filter profiles when search query changes
  useEffect(() => {
    if (!searchQuery || searchQuery.length < 3) {
      setFilteredProfiles(profiles);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filtered = profiles.filter((profile) => {
      const genericMatch = Object.keys(profile).some((k) =>
        String(profile[k]).toLowerCase().includes(query)
      );

      if (genericMatch) return true;

      const fName = profile.firstName?.toLowerCase() || '';
      const lName = profile.lastName?.toLowerCase() || '';
      const fullName = `${fName} ${lName}`;
      const fullNameReversed = `${lName} ${fName}`;

      return fullName.includes(query) || fullNameReversed.includes(query);
    });
    setFilteredProfiles(filtered);
  }, [searchQuery, profiles]);

  const columns = [
    { field: 'id', headerName: 'ID', width: 80 },
    createProfileIdentityColumn({ headerName: 'Profile' }),
    { field: 'email', headerName: 'Email', minWidth: 180, flex: 1 },
    { field: 'teamName', headerName: 'Team Name', minWidth: 150, flex: 1 },
  ];

  const handleRowClick = (params) => {
    navigate(`/profile/create/skillko/${site.id}/${params.row.id}/${params.row.teamId || 'null'}`);
  };

  const Item = styled(Paper)(({ theme }) => ({
    backgroundColor: '#F9FAFB',
    ...theme.typography.h5,
    padding: theme.spacing(1),
    textAlign: 'center',
    color: '#919EAB',
  }));

  const MobileRowContent = ({ row }) => (
    <Box sx={{ p: 1 }}>
      <Stack spacing={1}>
        <ProfileListIdentityCell row={row} />
        <Box>
          <Typography variant="subtitle2" color="text.secondary">Email</Typography>
          <Typography>{row.email}</Typography>
        </Box>
        <Box>
          <Typography variant="subtitle2" color="text.secondary">Team Name</Typography>
          <Typography>{row.teamName}</Typography>
        </Box>
        <Box>
          <Button
            variant="outlined"
            style={{color: '#1E88E5'}}
            disabled={!canCreateProfile}
            onClick={() => handleRowClick({ row })}
            fullWidth
          >
            Register
          </Button>
        </Box>
      </Stack>
    </Box>
  );

  const mobileColumns = [
    {
      field: 'mobileView',
      headerName: '',
      flex: 1,
      sortable: false,
      filterable: false,
      renderCell: (params) => <MobileRowContent row={params.row} />,
    }
  ];

  const updatedColumns = [
    ...columns,
    {
      field: 'action',
      headerName: 'Action',
      flex: isMobile ? 2 : 1,
      renderCell: (params) => (
        <Button
          variant="outlined"
          style={{color: '#1E88E5'}}
          disabled={!canCreateProfile}
          onClick={() => handleRowClick(params)}
        >
          Register
        </Button>
      ),
    },
  ];

  return (
    <>
      {loading ? (
        <>
          <SuspenseDiv />
        </>
      ) : filteredProfiles.length === 0 ? (
        <Grid container spacing={2}>
          <Grid xs={12} sx={{ mt: 5 }}>
            <Item>No profiles to display</Item>
          </Grid>
        </Grid>
      ) : (
        <Card>
          <ResizableDataGrid
            rows={filteredProfiles}
            columns={isMobile ? mobileColumns : updatedColumns}
            autoHeight
            rowHeight={isMobile ? 'auto' : undefined}
            getRowHeight={() => 'auto'}
            sx={{
              backgroundColor: 'white',
              borderRadius: 2,
              px: { xs: 1, sm: 3 },
              py: 5,
            }}
          />
        </Card>
      )}
    </>
  );
};

export default ProfilesListSkillkoTableInductionComplete;
